/*
 *   Copyright (c) 2023 Propally.Inc
 *   All rights reserved.
 */

import { Adjust, AdjustConfig, AdjustEnvironment, AdjustEvent } from "@awesome-cordova-plugins/adjust";
import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { PushNotifications, PushNotificationSchema, Token } from "@capacitor/push-notifications";
import { ScreenOrientation } from "@capacitor/screen-orientation";
import { StatusBar, Style } from "@capacitor/status-bar";
import { FirebaseAnalytics } from "@capacitor-firebase/analytics";
import { Badge } from "@capawesome/capacitor-badge";
import { AppTrackingTransparency } from "capacitor-plugin-app-tracking-transparency";
import dayjs from "dayjs";

import { DeviceTypeEnum } from "@/api/generated";
import { semanticColors } from "@/designTokens";

import { classifyNotificationCategory } from "./notificationClassification";
import {
  isNotificationsDisallowedError,
  isDeviceRegistrationLimitError,
  isTransientPushRegistrationError,
} from "./pushNotificationErrors";
import { recordPushRegistrationFailure, recordPushRegistrationSuccess } from "./pushRegistrationMonitor";
import { removeAppDeviceToken, setAppDeviceToken } from "./storage";

/*
 *   アプリ版かどうか
 */
export const isNativeApp = Capacitor.getPlatform() !== "web";

/*
 *   iOS端末かどうか
 */
export const isIos = Capacitor.getPlatform() === "ios";

/*
 *   Android端末かどうか
 */
export const isAndroid = Capacitor.getPlatform() === "android";

/*
 *   本番環境かどうか
 */
export const isProduction = process.env.NODE_ENV === "production";

let pushNotificationAppStateListenerAttached = false;
let defaultOrientationLocked = false;
let statusBarAppearanceInitialized = false;
const DEFAULT_STATUS_BAR_BACKGROUND_COLOR = semanticColors["surface/card"];

// MEMO: user_device_token.device_token はサーバ側で varchar(255) 制限。
const DEVICE_TOKEN_MAX_LENGTH = 255;

export const sanitizeDeviceToken = (token: unknown): string | null => {
  if (typeof token !== "string") return null;
  const trimmed = token.trim();
  if (!trimmed) return null;
  if (trimmed.length > DEVICE_TOKEN_MAX_LENGTH) return null;
  return trimmed;
};

/**
 * アプリ全体のデフォルト画面向きをポートレートに固定
 * Androidはmanifestで設定してしまうと以降の画面回転ができなくなるため、Capacitor初期化時にこちらで一度だけ呼び出す
 * iOSはinfo.plstでの初期値設定で十分
 */
export const initializeOrientationLock = (): void => {
  if (!isAndroid) return;
  if (defaultOrientationLocked) return;
  ScreenOrientation.lock({ orientation: "portrait" })
    .then(() => {
      defaultOrientationLocked = true;
    })
    .catch((error) => {
      if (process.env.NODE_ENV !== "production") {
        console.warn("初期ポートレートロックに失敗しました", error);
      }
    });
};

/**
 * ステータスバー表示をアプリ全体で統一する
 * MEMO:
 * Android は端末/OS により StatusBar プラグイン設定が見た目へ反映されないケースがある。
 * そのためネイティブ側も(MainActivity/styles.xml)でテーマ設定を行い、表示を担保する。
 */
export const initializeStatusBarAppearance = (): void => {
  if (!isNativeApp) return;
  if (statusBarAppearanceInitialized) return;
  statusBarAppearanceInitialized = true;

  // 両OSでコンテンツがステータスバー配下に潜り込まないようにする
  StatusBar.setOverlaysWebView({ overlay: false }).catch((error) => {
    if (process.env.NODE_ENV !== "production") {
      console.warn("StatusBar overlay 設定に失敗しました", error);
    }
  });

  // 両OSでステータスバーアイコンをライト背景向け（黒）に統一
  StatusBar.setStyle({ style: Style.Light }).catch((error) => {
    if (process.env.NODE_ENV !== "production") {
      console.warn("StatusBar style 設定に失敗しました", error);
    }
  });

  // Androidでステータスバー背景色を統一
  if (isAndroid) {
    StatusBar.setBackgroundColor({ color: DEFAULT_STATUS_BAR_BACKGROUND_COLOR }).catch((error) => {
      if (process.env.NODE_ENV !== "production") {
        console.warn("StatusBar 背景色設定に失敗しました", error);
      }
    });
  }
};

export const resolveDeviceTypeForNativeApp = (): DeviceTypeEnum | null => {
  if (!isNativeApp) return null;
  if (isIos) return DeviceTypeEnum.Ios;
  if (isAndroid) return DeviceTypeEnum.Android;
  return DeviceTypeEnum.Other;
};

/**
 * プッシュ通知初期化処理
 *
 * ref https://capacitorjs.jp/docs/guides/push-notifications-firebase
 */
export const initializePushNotification = (): void => {
  // アプリでない場合は何もしない
  if (!isNativeApp) return;

  // MEMO
  // アプリ起動時とログアウト時(なぜログアウトを検知して発火できるかは不明)に実行される処理
  // そのため初回利用時にプッシュ通知の許可をせず、後から設定で通知を許可した場合にもアプリを一度killして開かれれば有効になるはず
  // Request permission to use push notifications
  // iOS will prompt user and return if they granted permission or not
  // Android will just grant without prompting
  const registerDeviceForPush = () =>
    PushNotifications.register()
      .then((r) => {
        console.log("プッシュ通知登録リクエスト_受付: ", r);
      })
      .catch((r) => {
        // MEMO
        // register() は iOS / Android とも内部で即 resolve するため、この catch には登録失敗が届かない
        // ネットワーク起因も権限未許可も含め、APNs / FCM の登録失敗はすべて registrationError リスナー側で扱っている
        // （＝下の権限未許可の分岐も現状は到達しない。ここに来るのは Capacitor ブリッジ側の失敗のみ）
        // ※ @capacitor/push-notifications 8.1.1 時点の実装に基づく。更新時は register() が reject し得ないか再確認すること
        if (isNotificationsDisallowedError(r)) {
          console.info("プッシュ通知初期化_権限未許可のためスキップ");
          return;
        }
        console.error("プッシュ通知初期化_失敗: ", r);
      });

  const ensureRegisteredIfGranted = () => {
    PushNotifications.checkPermissions()
      .then((status) => {
        if (status.receive === "granted") {
          registerDeviceForPush();
        }
      })
      .catch((error) => {
        console.error("プッシュ通知権限確認失敗: ", error);
      });
  };

  PushNotifications.requestPermissions()
    .then((result) => {
      if (result.receive === "granted") {
        console.log("プッシュ通知オン");
        // Register with Apple / Google to receive push via APNS/FCM
        registerDeviceForPush();
      } else {
        console.log("プッシュ通知オフ");
      }
    })
    .catch((error) => {
      console.error("プッシュ通知許可リクエスト失敗: ", error);
    });

  if (!pushNotificationAppStateListenerAttached) {
    pushNotificationAppStateListenerAttached = true;
    App.addListener("appStateChange", ({ isActive }) => {
      if (!isActive) return;
      ensureRegisteredIfGranted();
    }).catch((error) => {
      console.error("アプリ状態リスナー登録失敗: ", error);
    });
  }

  // MEMO
  // アプリ起動時とログアウト時(なぜログアウトを検知して発火できるかは不明)に実行される処理 ※アプリをkillしていない状態で再度開いた時は開いた際は実行されない
  // On success, we should be able to receive notifications
  PushNotifications.addListener("registration", (token: Token) => {
    const sanitizedToken = sanitizeDeviceToken(token?.value);
    if (!sanitizedToken) {
      console.info("無効なデバイストークンを受信したため保存をスキップしました");
      removeAppDeviceToken();
      return;
    }

    console.log("トークン取得: ", sanitizedToken);

    // ログイン時にユーザIDとtoken.value(デバイスID)を紐づけるためにローカルストレージにデバイスIDを保存しておく
    setAppDeviceToken(sanitizedToken);
    void recordPushRegistrationSuccess();
    Adjust.setPushToken(sanitizedToken); // Adjust への送信
  });

  // MEMO
  // プッシュ通知登録失敗時の処理を記述
  // Some issue with our setup and push will not work
  PushNotifications.addListener("registrationError", (error: any) => {
    if (isNotificationsDisallowedError(error)) {
      console.info("プッシュ通知登録失敗(権限未許可): ", JSON.stringify(error));
      return;
    }
    if (isDeviceRegistrationLimitError(error)) {
      void recordPushRegistrationFailure(error, Capacitor.getPlatform());
      console.info("プッシュ通知登録失敗(FCM登録数上限): ", JSON.stringify(error));
      return;
    }
    if (isTransientPushRegistrationError(error)) {
      void recordPushRegistrationFailure(error, Capacitor.getPlatform());
      console.info("プッシュ通知登録失敗(一時的なネットワークエラー): ", JSON.stringify(error));
      return;
    }
    console.error("プッシュ通知登録失敗: ", JSON.stringify(error));
  });

  // MEMO
  // いったん特に利用はしないためコメントアウト
  // アプリからログアウトした際に溜まっているプッシュ通知を取得してalert()等で表示可能. なぜログアウトを検知して発火できるかは不明
  //
  // PushNotifications.getDeliveredNotifications().then((deliveredNotification) => {
  //   alert("デリバード: " + JSON.stringify(deliveredNotification));
  // });

  // MEMO
  // いったんは特に利用しないためコメントアウト
  // pushNotificationReceivedはアプリを開いている時にプッシュ通知が来た際に実行される処理を記述する
  // notificationはPushNotificationSchemaの型定義を確認すれば分かるが、オブジェクトでtitleやbody, badge等が格納されておりそれらをalert()等で表示可能
  // Show us the notification payload if the app is open on our device
  //
  PushNotifications.addListener("pushNotificationReceived", (notification: PushNotificationSchema) => {
    // MEMO: デバッグ用。通知内容を表示
    // console.log("プッシュ受信");
    // alert("Push received: " + JSON.stringify(notification));

    // URLに応じて受信イベントを発火
    const category = classifyNotificationCategory(notification.data?.route, notification.body);

    if (category === "journal") {
      sendEventToAppMeasurementTool(AppEventTokenEnum.NotificationJournalReceive);
    } else if (category === "assessmentProposal") {
      sendEventToAppMeasurementTool(AppEventTokenEnum.NotificationAssessmentProposalReceive);
    } else if (category === "investmentProposal") {
      sendEventToAppMeasurementTool(AppEventTokenEnum.NotificationInvestmentProposalReceive);
    } else {
      // 意図しない通知のため計測しない
    }
  });

  // MEMO
  // いったんは特に利用しないためコメントアウト
  // アプリ側で通知センター等に表示されているプッシュ通知を押してアプリを開いた際に実行される処理を記述する
  // notificationはActionPerformedの型定義を確認すれば分かるが、中にフィールドに「notification: PushNotificationSchema;」を持っており、
  // PushNotificationSchemaにはtitleやbody, badge等が格納されているため、それらをalert()等で表示可能
  // Method called when tapping on a notification
  //
  // こちらでプッシュ通知のイベントを取ろうとすると、
  // 「src/NotificationRouter.tsx」にてイベントが取れなくなるため、有効化しない
  // PushNotifications.addListener("pushNotificationActionPerformed", (action: ActionPerformed) => {
  //   // MEMO: デバッグ用。通知内容を表示
  //   // console.log("プッシュ通知タップ時");
  // });
};

/**
 *   計測ツール初期化処理
 *
 *   計測ツールはAdjustを利用
 *   参考: https://github.com/adjust/cordova_sdk
 *   ※AppsFlyer等の別の計測ツールに変更する際はこの中のロジックのみ修正する
 */
export const initializeAppMeasurementTool = (): void => {
  if (!isProduction) {
    console.log("本番環境以外のため実際の計測ツール初期化処理は行わず本ログ出力のみ実施");
    return;
  }

  // アプリでない場合は何もしない
  if (!isNativeApp) return;

  // MEMO
  // トークンは管理画面から取得 https://suite.adjust.com/mobile-apps/fbzvwbfd4cu8/sdk-integration
  // トークンは変数化せずベタで指定しないと認識されなかった
  const adjustConfig = new AdjustConfig("fbzvwbfd4cu8", AdjustEnvironment.Production);
  // ATTダイアログ表示前に initSdk が走ると初回セッションが ATT 未許可状態で送られる。
  // ATTが確定次第即送信、最長でも30秒待って未確定なら送信する。
  adjustConfig.setAttConsentWaitingInterval(30);
  Adjust.initSdk(adjustConfig);
  console.log("計測ツール初期化処理完了");
};

export const initializeATT = (): void => {
  if (!isProduction) {
    console.log("本番環境以外のためATT初期化処理は行わず本ログ出力のみ実施");
    return;
  }

  // ios以外は処理スキップ
  if (!isIos) return;

  AppTrackingTransparency.requestPermission()
    .then((result) => {
      console.log("ATT status:", result.status);
    })
    .catch((error) => {
      console.error(error);
    });
};

/**
 * アプリアイコンのバッジ件数を設定/クリア（Capacitor App Badge プラグイン）
 * - プラグイン未導入やWeb環境では安全に no-op
 */
export const setAppBadgeCount = async (count: number): Promise<void> => {
  if (!isNativeApp) return;
  try {
    const safe = Math.max(0, Math.floor(count || 0));
    await Badge.set({ count: safe });
  } catch (_) {
    // 特に何もしない
  }
};

export const clearAppBadge = async (): Promise<void> => {
  if (!isNativeApp) return;
  try {
    await Badge.clear();
  } catch (_) {
    await setAppBadgeCount(0);
  }
};

/**
 *   計測ツール用イベント名Enum
 */
export enum AppEventTokenEnum {
  // eslint-disable-next-line no-unused-vars
  UserRegistrationCompleted = "q1vwtd",
  // eslint-disable-next-line no-unused-vars
  UserRegistrationGoogleCompleted = "nal358",
  // eslint-disable-next-line no-unused-vars
  UserRegistrationAppleCompleted = "myaktf",
  // eslint-disable-next-line no-unused-vars
  UserRegistrationFailed = "2zxp61",
  // eslint-disable-next-line no-unused-vars
  UserAccountCompleted = "tc2u55",
  // eslint-disable-next-line no-unused-vars
  UserMypageAccess = "qyotwr",
  // eslint-disable-next-line no-unused-vars
  CashflowFirstAccess = "mh14v3",
  // eslint-disable-next-line no-unused-vars
  CashflowSecondOnwardsAccess = "uhxng9",
  // eslint-disable-next-line no-unused-vars
  OwnPropertyBasicInfoCompleted = "sfww6n",
  // eslint-disable-next-line no-unused-vars
  OwnPropertyPriceLoanCompleted = "2j9nru",
  // eslint-disable-next-line no-unused-vars
  OwnPropertyCompleted = "pl1t6x",
  // eslint-disable-next-line no-unused-vars
  OwnPropertyAiInputStart = "o46hyb",
  // eslint-disable-next-line no-unused-vars
  OwnPropertyAiInputFailed = "w3ws58",
  // eslint-disable-next-line no-unused-vars
  OwnPropertyAiInputSuccess = "clco8z",
  // eslint-disable-next-line no-unused-vars
  ConsiderationPropertyBasicInfoCompleted = "mb7tuy",
  // eslint-disable-next-line no-unused-vars
  ConsiderationPropertyPriceLoanCompleted = "368a0m",
  // eslint-disable-next-line no-unused-vars
  ConsiderationPropertyCompleted = "kglxxt",
  // eslint-disable-next-line no-unused-vars
  ConsiderationPropertyAiInputStart = "pxdqz9",
  // eslint-disable-next-line no-unused-vars
  ConsiderationPropertyAiInputFailed = "9djarh",
  // eslint-disable-next-line no-unused-vars
  ConsiderationPropertyAiInputSuccess = "dv1ktx",
  // eslint-disable-next-line no-unused-vars
  PropertyNearbyPriceFirstAccess = "sx4sr4",
  // eslint-disable-next-line no-unused-vars
  PropertyNearbyPriceSecondOnwardsAccess = "yg59dq",
  // eslint-disable-next-line no-unused-vars
  MypageFirstAccess = "81sm78",
  // eslint-disable-next-line no-unused-vars
  MypageSecondOnwardsAccess = "dode27",
  // eslint-disable-next-line no-unused-vars
  ConsiderationPropertyManagementFirstAccess = "mhgu22",
  // eslint-disable-next-line no-unused-vars
  ConsiderationPropertyManagementSecondOnwardsAccess = "drr6sd",
  // eslint-disable-next-line no-unused-vars
  OwnPropertyManagementFirstAccess = "ddv6uu",
  // eslint-disable-next-line no-unused-vars
  OwnPropertyManagementSecondOnwardsAccess = "evi0e3",
  // eslint-disable-next-line no-unused-vars
  // PropertyManagementFirstAccess = "3ds719", // 保有中/検討中で画面が分かれたため、現状使用していない。Adjustでは非表示としている
  // eslint-disable-next-line no-unused-vars
  // PropertyManagementSecondOnwardsAccess = "dwrnbd",　// 保有中/検討中で画面が分かれたため、現状使用していない。Adjustでは非表示としている
  // eslint-disable-next-line no-unused-vars
  OfferFirstAccess = "ea8rp1",
  // eslint-disable-next-line no-unused-vars
  OfferSecondOnwardsAccess = "xvjnsl",
  // eslint-disable-next-line no-unused-vars
  SettingFirstAccess = "o6aed4",
  // eslint-disable-next-line no-unused-vars
  SettingSecondOnwardsAccess = "8u7cl2",
  // eslint-disable-next-line no-unused-vars
  AgentSearchFirstAccess = "ht6kg5",
  // eslint-disable-next-line no-unused-vars
  AgentSearchSecondOnwardsAccess = "an6g5x",
  // eslint-disable-next-line no-unused-vars
  // TabOwnPropertyFirstAccess = "gr9s5j",　　// 保有中/検討中で画面が分かれたため、現状使用していない。Adjustでは非表示としている
  // eslint-disable-next-line no-unused-vars
  // TabOwnPropertySecondOnwardsAccess = "t139r3", // 保有中/検討中で画面が分かれたため、現状使用していない。Adjustでは非表示としている
  // eslint-disable-next-line no-unused-vars
  // TabConsiderationPropertyFirstAccess = "9qosj5",　// 保有中/検討中で画面が分かれたため、現状使用していない。Adjustでは非表示としている
  // eslint-disable-next-line no-unused-vars
  // TabConsiderationSecondOnwardsAccess = "5c4xd5",　// 保有中/検討中で画面が分かれたため、現状使用していない。Adjustでは非表示としている
  // eslint-disable-next-line no-unused-vars
  PropertyTabBalanceOfPaymentsFirstAccess = "3kdxhw",
  // eslint-disable-next-line no-unused-vars
  PropertyTabBalanceOfPaymentsSecondOnwardsAccess = "e63rzz",
  // eslint-disable-next-line no-unused-vars
  PropertyTabSimulationFirstAccess = "476zsu",
  // eslint-disable-next-line no-unused-vars
  PropertyTabSimulationSecondOnwardsAccess = "3gmym5",
  // eslint-disable-next-line no-unused-vars
  PropertyTabInformationFirstAccess = "ya6ko8",
  // eslint-disable-next-line no-unused-vars
  PropertyTabInformationSecondOnwardsAccess = "6ha3nl",
  // eslint-disable-next-line no-unused-vars
  RegistrationTopAccess = "akkom3",
  // eslint-disable-next-line no-unused-vars
  RegistrationRegistrationAccess = "wysayz",
  // eslint-disable-next-line no-unused-vars
  RegistrationSmsCodeInputAccess = "ua8rs3",
  // eslint-disable-next-line no-unused-vars
  RegistrationSmsNumberInputAccess = "xm2j7w",
  // eslint-disable-next-line no-unused-vars
  RegistrationDetailTopAccess = "vo2td5",
  // eslint-disable-next-line no-unused-vars
  RegistrationDetailFirstAccess = "a941iy",
  // eslint-disable-next-line no-unused-vars
  RegistrationDetailSecondAccess = "oskie8",
  // eslint-disable-next-line no-unused-vars
  RegistrationDetailThirdAccess = "lbdr20",
  // eslint-disable-next-line no-unused-vars
  RegistrationDetailFourthAccess = "64el3k",
  // eslint-disable-next-line no-unused-vars
  RegistrationDetailFifthAccess = "3yffxd",
  // eslint-disable-next-line no-unused-vars
  RegistrationDetailSixthAccess = "6vn788",
  // eslint-disable-next-line no-unused-vars
  RegistrationDetailSeventhAccess = "q6tj84",
  // eslint-disable-next-line no-unused-vars
  SendUserProposal = "wr5tkp",
  // eslint-disable-next-line no-unused-vars
  SendUserProposalTargetMatched = "agopd5",
  // eslint-disable-next-line no-unused-vars
  RecommendedPlannerDetailTap = "ujkkmd",
  // eslint-disable-next-line no-unused-vars
  RecommendedPlannerListAccess = "2sr4ne",
  // eslint-disable-next-line no-unused-vars
  RecommendedPlannerMatchConfirmAccess = "7e54yt",
  // eslint-disable-next-line no-unused-vars
  RecommendedPlannerDetailMatch = "7bwt7c",
  // eslint-disable-next-line no-unused-vars
  ApproveProposal = "nzhg08",
  // eslint-disable-next-line no-unused-vars
  ReadProposal = "bzoq1z",
  // MEMO 以降はrenewal_v1前に利用していたイベントで現在利用していない
  // eslint-disable-next-line no-unused-vars
  PropertyPriceCompleted = "iqet5f",
  // eslint-disable-next-line no-unused-vars
  PropertyLoanCompleted = "duigr1",
  // eslint-disable-next-line no-unused-vars
  PropertyCashflowCompleted = "ww0nxl",
  // eslint-disable-next-line no-unused-vars
  PropertyMiscCompleted = "iuafrm",
  // eslint-disable-next-line no-unused-vars
  DashboardFirstAccess = "8ouqhi",
  // eslint-disable-next-line no-unused-vars
  DashboardSecondOnwardsAccess = "ree9ux",
  // eslint-disable-next-line no-unused-vars
  ManagementFirstAccess = "6acbbp",
  // eslint-disable-next-line no-unused-vars
  ManagementSecondOnwardsAccess = "s9nthw",
  // eslint-disable-next-line no-unused-vars
  ManagementConsiderationFirstAccess = "xyem51",
  // eslint-disable-next-line no-unused-vars
  ManagementConsiderationSecondOnwardsAccess = "nxzj47",
  // eslint-disable-next-line no-unused-vars
  SimulationFirstAccess = "b8d32f",
  // eslint-disable-next-line no-unused-vars
  SimulationSecondOnwardsAccess = "7jtbf1",
  // eslint-disable-next-line no-unused-vars
  ComparisonFirstAccess = "ddih0t",
  // eslint-disable-next-line no-unused-vars
  ComparisonSecondOnwardsAccess = "a1j3ln",
  // eslint-disable-next-line no-unused-vars
  InvestmentFirstAccess = "e848zx",
  // eslint-disable-next-line no-unused-vars
  InvestmentSecondOnwardsAccess = "rg9qm8",
  // eslint-disable-next-line no-unused-vars
  AssessmentFirstAccess = "le7dxn",
  // eslint-disable-next-line no-unused-vars
  AssessmentSecondOnwardsAccess = "8suvfe",
  // eslint-disable-next-line no-unused-vars,
  ActionedInvestmentFirstAccess = "y2swcz",
  // eslint-disable-next-line no-unused-vars,
  ActionedInvestmentSecondOnwardsAccess = "zi9rag",
  // eslint-disable-next-line no-unused-vars,
  ActionedAssessmentFirstAccess = "aycgka",
  // eslint-disable-next-line no-unused-vars,
  ActionedAssessmentSecondOnwardsAccess = "iyqa34",
  // eslint-disable-next-line no-unused-vars,
  AccountFirstAccess = "87upq8",
  // eslint-disable-next-line no-unused-vars,
  AccountSecondOnwardsAccess = "ghi35a",
  // eslint-disable-next-line no-unused-vars,
  SignoutFirstAccess = "bryjv6",
  // eslint-disable-next-line no-unused-vars,
  SignoutSecondOnwardsAccess = "2m5k4y",
  // eslint-disable-next-line no-unused-vars,
  ReportFirstAccess = "cxi4v9",
  // eslint-disable-next-line no-unused-vars,
  ReportSecondOnwardsAccess = "jehjxh",
  // eslint-disable-next-line no-unused-vars,
  NotificationAssessmentProposalReceive = "919x97",
  // eslint-disable-next-line no-unused-vars,
  NotificationAssessmentProposalTap = "y9e7fo",
  // eslint-disable-next-line no-unused-vars,
  NotificationInvestmentProposalReceive = "wmu1lf",
  // eslint-disable-next-line no-unused-vars,
  NotificationInvestmentProposalTap = "iijx9g",
  // eslint-disable-next-line no-unused-vars,
  NotificationJournalReceive = "wuwjqk",
  // eslint-disable-next-line no-unused-vars,
  NotificationJournalTap = "qarvcr",
  // eslint-disable-next-line no-unused-vars,
  DeleteAccountRequest = "ieb477",
  // eslint-disable-next-line no-unused-vars,
  NotifyInflowChannel = "h2y0zg",
  // eslint-disable-next-line no-unused-vars,
  ClickLearningArticleCta = "2zfyoh",
  // eslint-disable-next-line no-unused-vars,
  InvestmentDiagnosisStep1 = "d5jf2u",
  // eslint-disable-next-line no-unused-vars,
  InvestmentDiagnosisStep2Loading = "1yv46i",
  // eslint-disable-next-line no-unused-vars,
  InvestmentDiagnosisStep2NG = "607fr1",
  // eslint-disable-next-line no-unused-vars,
  InvestmentDiagnosisStep2OK = "gc7d7p",
  // eslint-disable-next-line no-unused-vars,
  InvestmentDiagnosisStep3 = "5krlu3",
  // eslint-disable-next-line no-unused-vars,
  InvestmentDiagnosisStep4 = "cr8ne6",
  // eslint-disable-next-line no-unused-vars,
  InvestmentDiagnosisCompleteNG = "dfwyfh",
  // eslint-disable-next-line no-unused-vars,
  InvestmentDiagnosisCompleteOK = "cxlx5q",
  // eslint-disable-next-line no-unused-vars,
  PreInvestmentDiagnosisStep1 = "wy05iq",
  // eslint-disable-next-line no-unused-vars,
  PreInvestmentDiagnosisStep2 = "funsgr",
  // eslint-disable-next-line no-unused-vars,
  PreInvestmentDiagnosisStep3 = "3d4bsd",
  // eslint-disable-next-line no-unused-vars,
  PreInvestmentDiagnosisLoading = "xltabx",
  // eslint-disable-next-line no-unused-vars,
  PreInvestmentDiagnosisComplete = "sjfv6y",
  // eslint-disable-next-line no-unused-vars,
  InvestmentExperience = "rjdkqp",
  // eslint-disable-next-line no-unused-vars,
  InvestmentStyleDiagnosisIntroAccess = "oxr8f1",
  // eslint-disable-next-line no-unused-vars,
  InvestmentStyleDiagnosisPurposeAccess = "yeev4o",
  // eslint-disable-next-line no-unused-vars,
  InvestmentStyleDiagnosisInterestAccess = "882r72",
  // eslint-disable-next-line no-unused-vars,
  InvestmentStyleDiagnosisSuccessAccess = "lgu02m",
  // eslint-disable-next-line no-unused-vars,
  InvestmentStyleDiagnosisMonthlyAccess = "b305sf",
  // eslint-disable-next-line no-unused-vars,
  InvestmentStyleDiagnosisHoldingAccess = "m8eiz7",
  // eslint-disable-next-line no-unused-vars,
  InvestmentStyleDiagnosisLocationYieldAccess = "sb0el6",
  // eslint-disable-next-line no-unused-vars,
  InvestmentStyleDiagnosisManagementAccess = "ptz3go",
  // eslint-disable-next-line no-unused-vars,
  InvestmentStyleDiagnosisSaleTimingAccess = "rats9z",
  // eslint-disable-next-line no-unused-vars,
  InvestmentStyleDiagnosisMeetingItemsAccess = "a7u23e",
  // eslint-disable-next-line no-unused-vars,
  InvestmentStyleDiagnosisExplanationAccess = "l8cv21",
  // eslint-disable-next-line no-unused-vars,
  InvestmentStyleDiagnosisAnxietyAccess = "evpeok",
  // eslint-disable-next-line no-unused-vars,
  InvestmentStyleDiagnosisAvoidAccess = "be33q9",
  // eslint-disable-next-line no-unused-vars,
  InvestmentStyleDiagnosisCompleted = "fal14d",
  // eslint-disable-next-line no-unused-vars,
  InvestmentStyleDiagnosisSubmitFailed = "rrwt9a",
  // eslint-disable-next-line no-unused-vars,
  InvestmentStyleDiagnosisResultAccess = "1aowk7",
  // eslint-disable-next-line no-unused-vars,
  NotificationTaxReturn = "h3m2zz",
  // eslint-disable-next-line no-unused-vars,
  NotificationSecondOpinion = "9ta3hb",
  // eslint-disable-next-line no-unused-vars,
  NotificationGuide = "xfgb3e",
  // eslint-disable-next-line no-unused-vars,
  NotificationMSINS = "7cy05v",
  // eslint-disable-next-line no-unused-vars,
  NotificationSMAUL = "t0v2dl",
}

/**
 * AppEventTokenEnum の値からキー名を取得
 */
const getAppEventTokenEnumKeyByValue = (eventTokenEnumValue: AppEventTokenEnum): string => {
  return Object.keys(AppEventTokenEnum).find((key) => AppEventTokenEnum[key] === eventTokenEnumValue);
};

/**
 * サイドバーイベント送信時の 初回起動 or 2回目以降起動の判定ロジック
 * アプリkillを取得するのが難しかったため、代替えの判定として「ユーザ登録日時から24h以内のアクセス」を初回起動とすることとした
 */
export const isFirstAccess = (created_at: string): boolean => {
  const now = dayjs();
  const createdAt = dayjs(created_at);
  return now.diff(createdAt, "hour") <= 24;
};

/**
 *   アプリ計測ツールへのアプリ内イベント送信処理
 */
export const sendEventToAppMeasurementTool = (
  eventTokenEnum: AppEventTokenEnum,
  params?: { [key: string]: string | number | boolean }
): void => {
  // 計測ツールはAdjust & Firebaseを利用 ※AppsFlyer等の別の計測ツールに変更する際はこの中のロジックのみ修正する

  const eventName = getAppEventTokenEnumKeyByValue(eventTokenEnum);
  if (!isProduction) {
    const prettyParams = params ? JSON.stringify(params) : "{}";
    console.log(
      `本番環境以外のため計測ツールへのイベント送信は行わず本ログ出力のみ実施。eventToken: ${eventTokenEnum}, eventName: ${eventName}, params: ${prettyParams}`
    );
    // デバッグ用アラート表示
    // showAdjustDebugToast({ title: `DEBUG Adjust: ${eventName}`, token: String(eventTokenEnum), params });
    return;
  }

  // アプリでない場合は何もしない
  if (!isNativeApp) return;

  sendAdjustEvent(eventTokenEnum, params);
  sendFirebaseLogEvent(eventName, params);
};

/**
 *   Adjust計測へのイベント送信処理
 */
const sendAdjustEvent = (eventToken: string, params?: { [key: string]: string | number | boolean }) => {
  const ev = new AdjustEvent(eventToken);
  // Adjust へは callback parameters として渡す
  if (params) {
    Object.entries(params).forEach(([k, v]) => {
      try {
        // AdjustEvent の API では文字列での受け渡しが基本のため文字列化
        (ev as any).addCallbackParameter?.(k, String(v));
      } catch (_) {
        // 型や API 差異で失敗しても計測自体は継続
      }
    });
  }
  // デバッグ用アラート表示
  // showAdjustDebugToast({ title: `Adjust: ${eventToken}`, token: String(eventToken), params });

  Adjust.trackEvent(ev);
};

/**
 *   Firebase計測へのイベント送信処理
 */
const sendFirebaseLogEvent = (eventName: string, params?: Object) => {
  FirebaseAnalytics.logEvent({
    name: eventName,
    params: params ? params : {},
  }).then(() => {});
};
